import { useMemo, useRef, useState } from 'react';
import { TagChipPicker } from '@/components/features/mindspace/filters/TagChipPicker';
import {
  useDomainTagOptions,
  useSourceLessonOptions,
  useUserTagOptions,
} from '@/hooks/mindspace/useMindspaceOptions';
import { usePracticeQueue } from '@/hooks/mindspace/useRevision';
import type { SessionConfig } from '@/lib/practice/session-types';
import {
  buildSteps,
  effectiveSessionSize as computeSessionSize,
  indexOfStep,
  isCappedByLength,
  type StepKey,
} from '@/lib/practice/wizardSteps';
import { Stepper } from './WizardStepper';
import { StepShell } from './WizardStepShell';
import { ReviewStep } from './WizardReviewStep';
import { SourceLessonList } from './SourceLessonList';
import { WizardPhoneStepper } from './WizardPhoneStepper';
import { WizardLengthStep } from './WizardLengthStep';
import { WizardFooter } from './WizardFooter';
import { FilterLayoutContext } from '@/components/features/mindspace/filters/filterLayout';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';

const PREVIEW_LIMIT = 100;

const EMPTY_AVAILABLE = new Set<string>();

interface CustomSessionPanelProps {
  initialLength: number | 'all';
  /** Seed the domain-tag selection - used by quick-filter chips on the
   *  setup screen so clicking "Algebra" pre-selects it inside the modal. */
  initialDomain?: string[];
  onStart: (partial: Omit<SessionConfig, 'mode'>) => void;
}

export function CustomSessionPanel({
  initialLength,
  initialDomain,
  onStart,
}: CustomSessionPanelProps) {
  const [domain, setDomain] = useState<string[]>(initialDomain ?? []);
  const [user, setUser] = useState<string[]>([]);
  const [lessons, setLessons] = useState<string[]>([]);
  const [length, setLength] = useState<number | 'all'>(initialLength);
  const phone = useMediaQuery('(max-width: 639.98px)');

  const { data: domainOptions, isLoading: loadingDomain } =
    useDomainTagOptions();
  const { data: userOptions, isLoading: loadingUser } = useUserTagOptions();
  const { data: lessonOptions } = useSourceLessonOptions();

  // Live "how many concepts will I get?" preview - same query the runner
  // uses (questionsPerConcept: 0 skips LLM). `includeNotDue` mirrors the
  // real custom session so the count reflects every match, not just the due
  // subset. Drives the stepper count chip + the Review total. Tags always
  // combine as 'any' (union) - the default in usePracticeQueue.
  const matchPreview = usePracticeQueue({
    domain: domain.length ? domain : undefined,
    user: user.length ? user : undefined,
    lessons: lessons.length ? lessons : undefined,
    includeNotDue: true,
    questionsPerConcept: 0,
    limit: PREVIEW_LIMIT,
  });
  // `total` is the true match count before the response cap, so we report it
  // accurately even when the concept list is capped at PREVIEW_LIMIT (§2.11
  // #37). The size arithmetic below it lives in `lib/practice/wizardSteps`.
  const rawCount =
    matchPreview.data?.total ?? matchPreview.data?.concepts.length ?? null;
  const effectiveSessionSize = computeSessionSize(rawCount, length);
  const isCapped = isCappedByLength(rawCount, length);

  const toggle = (
    list: string[],
    setter: (next: string[]) => void,
    value: string
  ) => {
    setter(
      list.includes(value) ? list.filter(v => v !== value) : [...list, value]
    );
  };

  // The Lessons step is DROPPED, not disabled, when there is nothing to pick
  // (§2.11 #35) — so the indices shift and every jump goes through a key.
  const activeSteps = useMemo(
    () => buildSteps((lessonOptions?.length ?? 0) > 0),
    [lessonOptions]
  );

  // Position is tracked by KEY, not by index. The Lessons step appears when
  // its options query lands (§2.11 #35), and every step after it shifts down
  // one when it does — so an index captured before that moment points at a
  // DIFFERENT step afterwards, and a user who had reached Review on a slow
  // connection was silently moved back to Length. A key survives the list
  // changing underneath it.
  const [stepKey, setStepKey] = useState<StepKey>('domain');
  const stepIndex = Math.max(0, indexOfStep(activeSteps, stepKey));
  const current = activeSteps[stepIndex] ?? activeSteps[0];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === activeSteps.length - 1;

  const goToIndex = (index: number) => {
    const clamped = Math.min(Math.max(index, 0), activeSteps.length - 1);
    setStepKey(activeSteps[clamped].key);
  };
  const goTo = (key: StepKey) => {
    if (indexOfStep(activeSteps, key) >= 0) setStepKey(key);
  };

  // Single-shot guard so a double-click on "Start session" can't enqueue
  // two parallel queue fetches. The modal closes right after the first
  // call so the ref need not be reset.
  const startingRef = useRef(false);
  const submit = () => {
    if (startingRef.current) return;
    startingRef.current = true;
    onStart({
      scope: 'custom',
      length,
      domain: domain.length ? domain : undefined,
      user: user.length ? user : undefined,
      lessons: lessons.length ? lessons : undefined,
    });
  };

  return (
    // No outer chrome - this panel always renders inside a parent
    // container (Modal body) that supplies its own padding + border.
    <div className={phone ? 'flex h-full flex-col' : 'flex flex-col'}>
      {phone ? (
        <WizardPhoneStepper
          steps={activeSteps}
          currentIndex={stepIndex}
          matchCount={rawCount}
          isLoading={matchPreview.isLoading}
        />
      ) : (
        <Stepper
          steps={activeSteps}
          currentIndex={stepIndex}
          onJump={goToIndex}
          matchCount={rawCount}
          isLoading={matchPreview.isLoading}
        />
      )}

      {/* A phone's sheet: the step scrolls in the room the sheet leaves, its
          controls are the filter sheet's (44px chips, no inner scroller). */}
      <FilterLayoutContext.Provider value={phone ? 'sheet' : 'panel'}>
        <div
          className={
            phone
              ? 'min-h-0 flex-1 overflow-y-auto px-4 py-4'
              : 'h-[480px] overflow-y-auto px-6 py-5'
          }
        >
          {current.key === 'domain' && (
            <StepShell
              title='Pick the domains you want to practice'
              hint='Tags drawn from the topics across your mindspace. Skip to practice every domain.'
              selectionLabel={
                domain.length > 0
                  ? `${domain.length} domain${domain.length === 1 ? '' : 's'} selected`
                  : null
              }
              onClear={() => setDomain([])}
            >
              <TagChipPicker
                options={domainOptions ?? []}
                selected={domain}
                onToggle={tag => toggle(domain, setDomain, tag)}
                availableInView={EMPTY_AVAILABLE}
                isLoading={loadingDomain}
                emptyLabel='No domain tags yet.'
                dimUnavailable={false}
                size='lg'
              />
            </StepShell>
          )}

          {current.key === 'user' && (
            <StepShell
              title='Narrow by your own tags'
              hint='Tags you have added to concepts. Skip if you want all tags.'
              selectionLabel={
                user.length > 0
                  ? `${user.length} tag${user.length === 1 ? '' : 's'} selected`
                  : null
              }
              onClear={() => setUser([])}
            >
              <TagChipPicker
                options={userOptions ?? []}
                selected={user}
                onToggle={tag => toggle(user, setUser, tag)}
                availableInView={EMPTY_AVAILABLE}
                isLoading={loadingUser}
                emptyLabel='Add tags from a concept to filter by them.'
                dimUnavailable={false}
                size='md'
              />
            </StepShell>
          )}

          {current.key === 'lessons' && (
            <StepShell
              title='Pick source lessons'
              hint='Restrict practice to concepts that came from these lessons.'
              selectionLabel={
                lessons.length > 0
                  ? `${lessons.length} lesson${lessons.length === 1 ? '' : 's'} selected`
                  : null
              }
              onClear={() => setLessons([])}
            >
              <SourceLessonList
                options={lessonOptions ?? []}
                selected={lessons}
                onToggle={id => toggle(lessons, setLessons, id)}
              />
            </StepShell>
          )}

          {current.key === 'length' && (
            <StepShell
              title='How long should this session be?'
              hint='Cap the number of concepts, or remove the cap entirely.'
            >
              <WizardLengthStep length={length} onChange={setLength} />
            </StepShell>
          )}

          {current.key === 'review' && (
            <ReviewStep
              domain={domain}
              user={user}
              lessons={lessons}
              length={length}
              lessonTitleById={
                new Map((lessonOptions ?? []).map(l => [l.id, l.title]))
              }
              domainCountByTag={
                new Map((domainOptions ?? []).map(o => [o.tag, o.count]))
              }
              userCountByTag={
                new Map((userOptions ?? []).map(o => [o.tag, o.count]))
              }
              onEditDomain={() => goTo('domain')}
              onEditUser={() => goTo('user')}
              onEditLessons={() => goTo('lessons')}
              onEditLength={() => goTo('length')}
              showLessonsRow={(lessonOptions?.length ?? 0) > 0}
              effectiveSessionSize={effectiveSessionSize}
              isCapped={isCapped}
              isLoading={matchPreview.isLoading}
            />
          )}
        </div>
      </FilterLayoutContext.Provider>

      <WizardFooter
        phone={phone}
        isFirst={isFirst}
        isLast={isLast}
        canStart={rawCount !== 0}
        onBack={() => goToIndex(stepIndex - 1)}
        onNext={() => goToIndex(stepIndex + 1)}
        onStart={submit}
      />
    </div>
  );
}
